import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { hrmsGetRequest, hrmsPostRequest, hrmsUpdateRequest, hrmsDeleteRequest } from "../services/index";

const API_URL = "/api/hr/sessions";

// Fetch Sessions
export const fetchSessions = createAsyncThunk("sessions/fetch", async (_, { rejectWithValue }) => {
  try {
    const response = await hrmsGetRequest(API_URL);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Failed to fetch sessions");
  }
});

// Add Session
export const addSession = createAsyncThunk("sessions/add", async (sessionData, { rejectWithValue }) => {
  try {
    const response = await hrmsPostRequest(API_URL, sessionData);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Failed to add session");
  }
});

// Assign Teachers to Session
export const assignTeachersToSession = createAsyncThunk("sessions/assignTeachers", async ({ sessionId, teacherEmails }, { rejectWithValue }) => {
  try {
    const response = await hrmsUpdateRequest(`${API_URL}/${sessionId}/teachers`, { teacherEmails });
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Failed to assign teachers to session");
  }
});

// Get Session Teachers
export const getSessionTeachers = createAsyncThunk("sessions/getTeachers", async (sessionId, { rejectWithValue }) => {
  try {
    const response = await hrmsGetRequest(`${API_URL}/${sessionId}/teachers`);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Failed to get session teachers");
  }
});

// Get Sessions by Teacher Email
export const getSessionsByTeacherEmail = createAsyncThunk("sessions/getByTeacherEmail", async (teacherEmail, { rejectWithValue }) => {
  try {
    const response = await hrmsGetRequest(`${API_URL}/teacher/${teacherEmail}`);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Failed to get sessions by teacher email");
  }
});

// Update Session
export const updateSession = createAsyncThunk("sessions/update", async ({ id, updatedData }, { rejectWithValue }) => {
  try {
    const response = await hrmsUpdateRequest(`${API_URL}/${id}`, updatedData);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Failed to update session");
  }
});

// Delete Session
export const deleteSession = createAsyncThunk("sessions/delete", async (id, { rejectWithValue }) => {
  try {
    await hrmsDeleteRequest(`${API_URL}/${id}`);
    return id;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Failed to delete session");
  }
});

const sessionSlice = createSlice({
  name: "sessions",
  initialState: {
    sessions: [],
    sessionTeachers: {},
    teacherSessions: {},
    status: "idle",
    error: null,
    deleteStatus: "idle",
    assignStatus: "idle",
  },
  reducers: {
    resetDeleteStatus: (state) => {
      state.deleteStatus = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSessions.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchSessions.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.sessions = action.payload;
        state.error = null;
      })
      .addCase(fetchSessions.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(addSession.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addSession.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.sessions.push(action.payload);
        state.error = null;
      })
      .addCase(addSession.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(updateSession.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateSession.fulfilled, (state, action) => {
        state.status = "succeeded";
        const index = state.sessions.findIndex((s) => s.id === action.payload.id);
        if (index !== -1) {
          state.sessions[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(updateSession.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(deleteSession.pending, (state) => {
        state.deleteStatus = "loading";
      })
      .addCase(deleteSession.fulfilled, (state, action) => {
        state.deleteStatus = "succeeded";
        state.sessions = state.sessions.filter((s) => s.id !== action.payload);
        state.error = null;
      })
      .addCase(deleteSession.rejected, (state, action) => {
        state.deleteStatus = "failed";
        state.error = action.payload;
      })
      .addCase(assignTeachersToSession.pending, (state) => {
        state.assignStatus = "loading";
      })
      .addCase(assignTeachersToSession.fulfilled, (state, action) => {
        state.assignStatus = "succeeded";
        state.error = null;
      })
      .addCase(assignTeachersToSession.rejected, (state, action) => {
        state.assignStatus = "failed";
        state.error = action.payload;
      })
      .addCase(getSessionTeachers.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getSessionTeachers.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.sessionTeachers[action.meta.arg] = action.payload;
        state.error = null;
      })
      .addCase(getSessionTeachers.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(getSessionsByTeacherEmail.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getSessionsByTeacherEmail.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.teacherSessions[action.meta.arg] = action.payload;
        state.error = null;
      })
      .addCase(getSessionsByTeacherEmail.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export const { resetDeleteStatus: resetSessionDeleteStatus } = sessionSlice.actions;
export default sessionSlice.reducer;